import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Attachments from '../components/Attachments';
import Field from '../components/Field';
import Error from '../components/Error';
import styles from '../Modal.module.scss';
import { FieldValues } from '../types';

const filesLimit = 10;
const filesSizeLimit = 1024 * 1024 * 20;

const Step3 = (): React.ReactElement => {
  const [filesErrors, setFilesErrors] = useState<Record<number, string>>();
  const {
    register,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<FieldValues>();

  register('files', {
    validate: (value) => {
      let errors: Record<number, string> | undefined;
      let totalSize = 0;

      value.forEach((file, index) => {
        totalSize += file.size;
        let error = '';

        if (file.size > filesSizeLimit) {
          error = `File size must be less than ${Math.ceil(filesSizeLimit / 1024 / 1024)}MB`;
        } else if (file.name.endsWith('.exe')) {
          error = "File can't be execution program";
        }

        if (error) {
          if (!errors) {
            errors = {};
          }

          errors[index] = error;
        }
      });

      setFilesErrors(errors);

      if (totalSize > filesSizeLimit) {
        return `Total size must be less than ${Math.ceil(filesSizeLimit / 1024 / 1024)}MB`;
      }

      return !(value.length === 0 || value.length > filesLimit || totalSize === 0 || errors);
    },
  });

  const handleSetFiles = useCallback(
    (payload: FieldValues['files']) => {
      setValue('files', payload, { shouldValidate: true });
    },
    [setValue]
  );

  const assetClass = watch('assetClass');
  const files = watch('files');
  let researchLabel: string;

  if (assetClass === 'loan') {
    researchLabel = 'loan research';
  } else if (assetClass === 'bond') {
    researchLabel = 'bond research';
  } else {
    researchLabel = 'research';
  }

  return (
    <>
      <div className={styles.modalHeader}>Complete the form and submit</div>
      <div className={styles.modalSubHeader}>
        Enter the name of the borrower/issuer and upload the files to submit your {researchLabel} request.
      </div>
      <div className={styles.modalFormLine}>
        <div className={styles.modalFormLabel}>Name of borrower/issuer:</div>
        <Field error={errors.borrower?.message}>
          <input
            className={classNames(styles.modalFormInput)}
            type="text"
            placeholder="Enter here"
            {...register('borrower', {
              maxLength: { value: 500, message: 'The name can’t have more than 500 characters' },
            })}
          />
        </Field>
      </div>
      <div className={styles.modalFormLine}>
        <div className={styles.modalFormLabel}>Message:</div>
        <Field error={errors.message?.message}>
          <textarea
            className={classNames(styles.modalFormInput, { [styles.modalFormTextarea]: true })}
            placeholder="Enter here"
            {...register('message', {
              maxLength: { value: 2000, message: 'The message can’t have more than 2000 characters' },
            })}
          />
        </Field>
      </div>
      <div className={styles.modalFormLine}>
        <div className={styles.modalFormLabel}>
          Attachments <span>({files?.length ? `${files.length} / ${filesLimit}` : 'required'})</span>:{' '}
          {errors.files && files.length > 0 ? (
            <Error className={styles.modalFormLabelError} error={errors.files?.message || <>&nbsp;</>} />
          ) : null}
        </div>
        <Attachments
          onSetFiles={handleSetFiles}
          files={files}
          filesLimit={filesLimit}
          error={!!errors.files}
          messages={filesErrors}
        />
      </div>
    </>
  );
};

export default Step3;
